import styled, { keyframes } from 'styled-components';
import { Input, Select } from 'antd';

const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const spinAnimation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

export const ModalContent = styled.div`
  padding: 20px 20px 16px;
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 20px;
`;

export const IconContainer = styled.div`
  margin-bottom: 12px;
`;

export const CreateIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto;
  background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  box-shadow: 0 6px 16px rgba(31, 162, 255, 0.25);
`;

export const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

export const Subtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

export const FormSection = styled.div`
  margin-bottom: 20px;
`;

export const FormField = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

export const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 6px;
`;

export const StyledInput = styled(Input)`
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  transition: all 0.2s ease;
  
  &:hover {
    border-color: #1677ff;
  }
  
  &:focus, &.ant-input-focused {
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.45);
  }
`;

export const StyledSelect = styled(Select)<any>`
  width: 100%;
  
  .ant-select-selector {
    border-radius: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.15) !important;
    transition: all 0.2s ease;
  }
  
  &:hover .ant-select-selector {
    border-color: #1677ff !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: #1677ff !important;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1) !important;
  }
`;

export const YearDisplay = styled.div`
  margin-top: 6px;
  padding: 6px 10px;
  background: rgba(22, 119, 255, 0.08);
  border-radius: 6px;
  font-size: 12px;
  color: #1677ff;
  font-weight: 500;
`;

export const ButtonSection = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

export const CancelButton = styled.button`
  padding: 8px 16px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 6px;
  background: white;
  color: #666;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  
  &:hover:not(:disabled) {
    border-color: #1677ff;
    color: #1677ff;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const CreateButton = styled.button<{ isLoading?: boolean }>`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%);
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 100px;
  justify-content: center;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #12D8FA 0%, #1FA2FF 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(31, 162, 255, 0.3);
  }
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
  
  .anticon {
    font-size: 14px;
    ${props => props.isLoading && `
      animation: ${spinAnimation} 1s linear infinite;
    `}
  }
`;

// Mobile responsive styles
export const MobileModalContent = styled(ModalContent)`
  @media (max-width: 768px) {
    padding: 24px 16px 16px;
    
    ${Title} {
      font-size: 20px;
    }
    
    ${Subtitle} {
      font-size: 14px;
    }
    
    ${ButtonSection} {
      flex-direction: column;
      gap: 8px;
    }
    
    ${CancelButton}, ${CreateButton} {
      width: 100%;
      min-width: auto;
    }
  }
`;
